/* eslint-disable boundaries/element-types */
import { FormFieldController } from 'components/form/FormFieldController'
import { convertToMetricBaseUnit } from 'components/form/formHelpers'
import { FormItem } from 'components/form/FormItem'
import { SimpleFormField } from 'components/form/formTypes'
import { AddressInput } from 'components/form/inputs/AddressInput'
import { DateInput } from 'components/form/inputs/DateInput'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { MultiSelect } from 'components/form/inputs/MultiSelect'
import { NumberInput } from 'components/form/inputs/NumberInput'
import { TextInput } from 'components/form/inputs/TextInput'
import React from 'react'
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import {
  convertCoordinateString,
  FormField,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  InputType,
} from 'types'

type RenderFormFieldProps = {
  formItemKey: string
  formItem: FormField
  fieldValue: string | number | string[] | undefined
  fieldUnits?: string
  formControl: Control<SimpleFormField>
  formSetValue: UseFormSetValue<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
    [x: string]: any
  }>
  formErrors: FieldErrors<SimpleFormField>
  formTrigger: UseFormTrigger<SimpleFormField>
  borderColor?: string
  labelColor?: string
  units: GeckoMeasurementUnit | null
  fieldMode: 'edit' | 'read'
  formItemLabel: string | undefined
}

const StyledFormItem = styled(FormItem)`
  display: contents;
  border-radius: 6px;
`

export const RenderFormField: React.FC<RenderFormFieldProps> = ({
  formItemKey,
  formItem,
  fieldValue,
  fieldUnits,
  formControl,
  formSetValue,
  formErrors,
  formTrigger,
  borderColor,
  labelColor,
  fieldMode,
  formItemLabel,
  units,
}) => {
  const { theme } = useGeckoTheme()
  return (
    <>
      <FormFieldController
        control={formControl}
        name={formItemKey}
        render={({ field: { ...fieldProps } }) => {
          const inputType = formItem.inputType
          switch (inputType) {
            case InputType.Text:
            case InputType.TextLink:
            case InputType.TextArea:
              return (
                <StyledFormItem>
                  <TextInput
                    data-testid={`custom-form-field-${formItemLabel}`}
                    defaultValue={fieldValue ?? ''}
                    $subtitleText={formErrors[fieldProps.name]?.message}
                    $subtitleTextColor={theme.forecast.danger}
                    onChange={(e) => {
                      fieldProps.onChange(e.target.value)
                      formTrigger(fieldProps.name)
                    }}
                    $borderColor={borderColor}
                    $labelColor={labelColor}
                    $label={formItemLabel}
                    $labelLocation={
                      formItemLabel !== undefined ? 'side' : undefined
                    }
                    mode={fieldMode}
                    disabled={formItem.disabled}
                    type={
                      inputType === InputType.TextLink
                        ? 'link'
                        : inputType === InputType.TextArea
                          ? 'textarea'
                          : 'input'
                    }
                  />
                </StyledFormItem>
              )
            case InputType.Number:
              return (
                <NumberInput
                  defaultValue={fieldValue}
                  data-testid={`custom-form-field-${formItemLabel}`}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const input = parseFloat(e.target.value)
                    //all gecko measurement data is store in metric
                    const storedUnit = convertToMetricBaseUnit(
                      formItem.unit_magnitude
                    )
                    if (!Number.isNaN(input) && units) {
                      const convertedValue = GeckoMeasurement.CreateFromValue(
                        input,
                        units
                      ).convertTo(storedUnit ?? GeckoMeasurementUnit.Meters).num
                      fieldProps.onChange(convertedValue)
                    } else {
                      fieldProps.onChange(e.target.value)
                    }
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $labelColor={labelColor}
                  $label={formItemLabel}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  units={fieldUnits}
                  mode={fieldMode}
                  disabled={formItem.disabled}
                />
              )
            case InputType.Dropdown:
              return (
                <Dropdown
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue?.toString()}
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(e) => {
                    fieldProps.onChange(e)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  $itemKey={formItemKey}
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  placeholder='Select an option'
                  renderMenuItem={formItem.renderMenuItem}
                />
              )
            case InputType.Coordinates:
              return (
                <TextInput
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onChange={(e) => {
                    const input = e.target.value

                    const coordinateValue = units
                      ? convertCoordinateString(
                          input,
                          units,
                          GeckoMeasurementUnit.Meters
                        )
                      : null
                    fieldProps.onChange(coordinateValue)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  disabled={formItem.disabled}
                />
              )
            case InputType.Date:
              return (
                <DateInput
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onChange={(e) => {
                    fieldProps.onChange(e.target.value)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  disabled={formItem.disabled}
                />
              )
            case InputType.Address:
              return (
                <AddressInput
                  data-testid={`custom-form-field-${fieldProps.name}`}
                  defaultValue={fieldValue}
                  onChange={(e) => {
                    fieldProps.onChange(e.target.value)
                    formTrigger(fieldProps.name)
                  }}
                  formItemKey={formItemKey}
                  formSetValue={formSetValue}
                  $borderColor={borderColor}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  disabled={formItem.disabled}
                  formatAddressValueAsString={
                    formItem.formatAddressValueAsString
                  }
                />
              )
            case InputType.MultiSelect:
              return (
                <MultiSelect
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={fieldValue}
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(e) => {
                    fieldProps.onChange(e)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $itemKey={formItemKey}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  placeholder='Select options'
                  disabled={formItem.disabled}
                />
              )
            case InputType.Combobox:
              return (
                <Dropdown
                  data-testid={`custom-form-field-${formItemLabel}`}
                  defaultValue={
                    typeof fieldValue === 'string' ? fieldValue : undefined
                  }
                  options={formItem.options ?? []}
                  $subtitleText={formErrors[fieldProps.name]?.message}
                  onValueChange={(value: string) => {
                    fieldProps.onChange(value)
                    formTrigger(fieldProps.name)
                  }}
                  $borderColor={borderColor}
                  $subtitleTextColor={theme.forecast.danger}
                  $label={formItemLabel}
                  $labelColor={labelColor}
                  $itemKey={formItemKey}
                  $labelLocation={
                    formItemLabel !== undefined ? 'side' : undefined
                  }
                  mode={fieldMode}
                  placeholder='Select an option'
                  disabled={formItem.disabled}
                />
              )
            default:
              return (
                <StyledFormItem>
                  <TextInput
                    data-testid={`custom-form-field-${formItemLabel}`}
                    defaultValue={fieldValue ?? ''}
                    $subtitleText={formErrors[fieldProps.name]?.message}
                    $subtitleTextColor={theme.forecast.danger}
                    onChange={(e) => {
                      fieldProps.onChange(e.target.value)
                      formTrigger(fieldProps.name)
                    }}
                    $borderColor={borderColor}
                    $labelColor={labelColor}
                    $label={formItemLabel}
                    $labelLocation={
                      formItemLabel !== undefined ? 'side' : undefined
                    }
                    mode={fieldMode}
                    disabled={formItem.disabled}
                    type='input'
                  />
                </StyledFormItem>
              )
          }
        }}
      />
    </>
  )
}
